import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { usePersistedClientId } from "@/lib/clientSelection";
import { PageHeader } from "@/pages/layoutChrome";
import FieldHint from "@/components/laboral/FieldHint";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Briefcase, ClipboardList, Download, FileSpreadsheet, Loader2, Pencil, Plus, Trash2, Upload, Users } from "lucide-react";
import { toast } from "sonner";
import LaboralFlujoOperativo from "@/components/laboral/LaboralFlujoOperativo";
import { prestadorRepseFromClient } from "@/lib/clientLaboralProfile";

const CONTEXT_LABEL = "Asistente Laboral y REPSE";

function emptyContratoForm() {
  return {
    numero_contrato: "",
    razon_social_obra: "",
    registro_patronal_id: "",
    registro_patronal: "",
    tipo_prestacion: "",
    modalidad_obra: "",
    folio_registro_repse: "",
    rfc_contratante: "",
    razon_social_contratante: "",
    domicilio_obra: "",
    municipio_obra: "",
    estado_obra: "",
    actividad_especifica: "",
    numero_obra_imss: "",
    fecha_inicio: "",
    fecha_fin: "",
  };
}

function labelTipoModalidad(row, tipos, modalidades) {
  const t = tipos.find((x) => x.id === row.tipo_prestacion);
  const m = modalidades.find((x) => x.id === row.modalidad_obra);
  const parts = [t?.label, m?.label].filter(Boolean);
  return parts.length ? parts.join(" · ") : "—";
}

/**
 * Control REPSE: contratos/obra y asignación de trabajadores para cuatrimestrales.
 */
export default function ControlRepse() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = usePersistedClientId(clients);
  const [config, setConfig] = useState(null);
  const [patronales, setPatronales] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [form, setForm] = useState(emptyContratoForm);
  const [busy, setBusy] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState(emptyContratoForm());
  const [deleteRow, setDeleteRow] = useState(null);

  const [contratoAsignacionId, setContratoAsignacionId] = useState("");
  const [trabajadoresObra, setTrabajadoresObra] = useState([]);
  const [todosTrabajadores, setTodosTrabajadores] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loadingAsignacion, setLoadingAsignacion] = useState(false);
  const [desasignarIds, setDesasignarIds] = useState([]);
  const [anioCuat, setAnioCuat] = useState(() => new Date().getFullYear());
  const [cuatrimestreCuat, setCuatrimestreCuat] = useState("1");
  const [editCuat, setEditCuat] = useState({});
  const [movContratoId, setMovContratoId] = useState("");
  const [movTrabajadorId, setMovTrabajadorId] = useState("");
  const [movTipo, setMovTipo] = useState("alta");
  const [movFecha, setMovFecha] = useState("");
  const [movNota, setMovNota] = useState("");
  const [movimientos, setMovimientos] = useState([]);
  const [loadingMov, setLoadingMov] = useState(false);
  const [searchRelacionInput, setSearchRelacionInput] = useState("");
  const [searchRelacionQ, setSearchRelacionQ] = useState("");
  const [filterEstatusRelacion, setFilterEstatusRelacion] = useState("todos");
  const [contratosRelacion, setContratosRelacion] = useState([]);
  const [loadingRelacion, setLoadingRelacion] = useState(false);
  const [uploadContratoId, setUploadContratoId] = useState("");
  const pdfInputRef = useRef(null);

  const selectedClient = clients.find((c) => c.id === clientId) || null;
  const prestador = useMemo(
    () => config?.prestador || prestadorRepseFromClient(selectedClient),
    [config?.prestador, selectedClient],
  );
  const hints = config?.field_hints_contrato || {};
  const tipos = config?.tipos_prestacion || [];
  const modalidades = config?.modalidades_obra || [];

  const onField = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const loadPatronales = useCallback(async () => {
    if (!clientId) {
      setPatronales([]);
      return;
    }
    try {
      const { data } = await api.get("/laboral/registros-patronales", { params: { client_id: clientId } });
      setPatronales(Array.isArray(data.items) ? data.items : []);
    } catch {
      setPatronales([]);
    }
  }, [clientId]);

  const loadContratos = useCallback(async () => {
    if (!clientId) {
      setContratos([]);
      return;
    }
    setLoadingTable(true);
    try {
      const { data } = await api.get("/laboral/contratos-repse", { params: { client_id: clientId } });
      setContratos(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      setContratos([]);
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudieron cargar contratos REPSE");
    } finally {
      setLoadingTable(false);
    }
  }, [clientId]);

  const loadContratosRelacion = useCallback(async () => {
    if (!clientId) {
      setContratosRelacion([]);
      return;
    }
    setLoadingRelacion(true);
    try {
      const { data } = await api.get("/laboral/contratos-repse", {
        params: {
          client_id: clientId,
          q: searchRelacionQ,
          incluir_inactivos: true,
        },
      });
      setContratosRelacion(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      setContratosRelacion([]);
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo cargar la relación de contratos");
    } finally {
      setLoadingRelacion(false);
    }
  }, [clientId, searchRelacionQ]);

  const loadConfig = useCallback(async () => {
    if (!clientId) {
      setConfig(null);
      return;
    }
    try {
      const { data } = await api.get("/laboral/contratos-repse/config", { params: { client_id: clientId } });
      setConfig(data);
    } catch {
      setConfig(null);
    }
  }, [clientId]);

  const loadTrabajadoresObra = useCallback(async () => {
    if (!clientId || !contratoAsignacionId) {
      setTrabajadoresObra([]);
      return;
    }
    setLoadingAsignacion(true);
    try {
      const { data } = await api.get(`/laboral/contratos-repse/${contratoAsignacionId}/trabajadores`, {
        params: { client_id: clientId },
      });
      setTrabajadoresObra(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      setTrabajadoresObra([]);
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudieron cargar trabajadores de la obra");
    } finally {
      setLoadingAsignacion(false);
    }
  }, [clientId, contratoAsignacionId]);

  const loadMovimientos = useCallback(async () => {
    if (!clientId) { setMovimientos([]); return; }
    setLoadingMov(true);
    try {
      const { data } = await api.get("/laboral/movimientos-obra-repse", {
        params: { client_id: clientId, contrato_id: movContratoId || undefined },
      });
      setMovimientos(Array.isArray(data.items) ? data.items : []);
    } catch { setMovimientos([]); }
    finally { setLoadingMov(false); }
  }, [clientId, movContratoId]);

  const loadTodosTrabajadores = useCallback(async () => {
    if (!clientId) {
      setTodosTrabajadores([]);
      return;
    }
    try {
      const { data } = await api.get("/laboral/trabajadores-registro", { params: { client_id: clientId } });
      setTodosTrabajadores(Array.isArray(data.items) ? data.items : []);
    } catch {
      setTodosTrabajadores([]);
    }
  }, [clientId]);

  useEffect(() => {
    const t = setTimeout(() => setSearchRelacionQ(searchRelacionInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchRelacionInput]);

  useEffect(() => {
    loadContratosRelacion();
  }, [loadContratosRelacion]);

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data)).catch(() => setClients([]));
  }, []);

  useEffect(() => {
    loadConfig();
    loadPatronales();
    loadContratos();
    loadTodosTrabajadores();
    setForm(emptyContratoForm());
    setContratoAsignacionId("");
    setSelectedIds([]);
  }, [clientId, loadConfig, loadPatronales, loadContratos, loadTodosTrabajadores]);

  useEffect(() => {
    loadTrabajadoresObra();
  }, [loadTrabajadoresObra]);

  useEffect(() => { loadMovimientos(); }, [loadMovimientos]);

  const payloadFromForm = (f) => ({
    client_id: clientId,
    ...f,
  });

  const onCreate = async () => {
    if (!clientId) {
      toast.error("Seleccione un cliente de trabajo");
      return;
    }
    if (!form.numero_contrato.trim()) {
      toast.error("Indique el número de contrato");
      return;
    }
    if (!form.razon_social_obra.trim()) {
      toast.error("Indique el nombre de la obra");
      return;
    }
    if (!form.registro_patronal_id && !form.registro_patronal.trim()) {
      toast.error("Seleccione o capture el registro patronal de la obra");
      return;
    }
    if (!form.tipo_prestacion) {
      toast.error("Seleccione el tipo de prestación (servicios u obra especializada)");
      return;
    }
    if (!form.modalidad_obra) {
      toast.error("Seleccione la modalidad de obra");
      return;
    }
    setBusy(true);
    try {
      await api.post("/laboral/contratos-repse", payloadFromForm(form));
      toast.success("Contrato REPSE registrado");
      setForm(emptyContratoForm());
      await loadContratos();
      await loadContratosRelacion();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al registrar contrato");
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (row) => {
    setEditRow(row);
    setEditForm({
      numero_contrato: row.numero_contrato || "",
      razon_social_obra: row.razon_social_obra || "",
      registro_patronal_id: row.registro_patronal_id || "",
      registro_patronal: row.registro_patronal || "",
      tipo_prestacion: row.tipo_prestacion || "",
      modalidad_obra: row.modalidad_obra || "",
      folio_registro_repse: row.folio_registro_repse || "",
      rfc_contratante: row.rfc_contratante || "",
      razon_social_contratante: row.razon_social_contratante || "",
      domicilio_obra: row.domicilio_obra || "",
      municipio_obra: row.municipio_obra || "",
      estado_obra: row.estado_obra || "",
      actividad_especifica: row.actividad_especifica || "",
      numero_obra_imss: row.numero_obra_imss || "",
      fecha_inicio: row.fecha_inicio || "",
      fecha_fin: row.fecha_fin || "",
    });
  };

  const onSaveEdit = async () => {
    if (!editRow?.id) return;
    setBusy(true);
    try {
      await api.put(`/laboral/contratos-repse/${editRow.id}`, payloadFromForm(editForm));
      toast.success("Contrato actualizado");
      setEditRow(null);
      await loadContratos();
      await loadContratosRelacion();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al actualizar");
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!deleteRow?.id || !clientId) return;
    setBusy(true);
    try {
      await api.delete(`/laboral/contratos-repse/${deleteRow.id}`, { params: { client_id: clientId } });
      toast.success("Contrato desactivado");
      setDeleteRow(null);
      if (contratoAsignacionId === deleteRow.id) setContratoAsignacionId("");
      await loadContratos();
      await loadContratosRelacion();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al desactivar");
    } finally {
      setBusy(false);
    }
  };

  const triggerUploadPdf = (contratoId) => {
    setUploadContratoId(contratoId);
    pdfInputRef.current?.click();
  };

  const onPdfSelected = async (file) => {
    if (!file || !clientId || !uploadContratoId) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Seleccione un archivo PDF");
      return;
    }
    setBusy(true);
    try {
      const body = new FormData();
      body.append("client_id", clientId);
      body.append("archivo", file);
      await api.post(`/laboral/contratos-repse/${uploadContratoId}/documento/subir`, body, {
        timeout: 120000,
      });
      toast.success("PDF del contrato REPSE guardado");
      await loadContratos();
      await loadContratosRelacion();
      if (editRow?.id === uploadContratoId) {
        const { data } = await api.get("/laboral/contratos-repse", {
          params: { client_id: clientId, incluir_inactivos: true },
        });
        const updated = (data.items || []).find((c) => c.id === uploadContratoId);
        if (updated) setEditRow(updated);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo subir el PDF");
    } finally {
      setBusy(false);
      setUploadContratoId("");
      if (pdfInputRef.current) pdfInputRef.current.value = "";
    }
  };

  const onDownloadPdf = async (row) => {
    if (!clientId || !row?.id || !row.tiene_documento_pdf) return;
    setBusy(true);
    try {
      const response = await api.get(`/laboral/contratos-repse/${row.id}/documento/descargar`, {
        params: { client_id: clientId },
        responseType: "blob",
      });
      const blob = response.data;
      const cd = response.headers["content-disposition"] || "";
      const quoted = cd.match(/filename="([^"]+)"/i);
      const filename = quoted?.[1] || row.documento_filename || `Contrato_REPSE_${row.numero_contrato}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo descargar el PDF");
    } finally {
      setBusy(false);
    }
  };

  const onExportExcel = async () => {
    if (!clientId) return;
    setBusy(true);
    try {
      const response = await api.get("/laboral/contratos-repse/reporte-excel", {
        params: {
          client_id: clientId,
          q: searchRelacionQ,
          incluir_inactivos: true,
        },
        responseType: "blob",
        timeout: 120000,
      });
      const cd = response.headers["content-disposition"] || "";
      const quoted = cd.match(/filename="([^"]+)"/i);
      const filename = quoted?.[1] || "Relacion_Contratos_REPSE.xlsx";
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Reporte Excel descargado");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo generar el Excel");
    } finally {
      setBusy(false);
    }
  };

  const contratosRelacionFiltrados = useMemo(() => {
    if (filterEstatusRelacion === "todos") return contratosRelacion;
    return contratosRelacion.filter((r) => r.estatus_obra === filterEstatusRelacion);
  }, [contratosRelacion, filterEstatusRelacion]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const trabajadoresDisponibles = useMemo(() => {
    const asignados = new Set(trabajadoresObra.map((t) => t.id));
    return todosTrabajadores.filter((t) => !asignados.has(t.id));
  }, [todosTrabajadores, trabajadoresObra]);


  const toggleDesasignar = (id) => {
    setDesasignarIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const onDesasignar = async () => {
    if (!clientId || !contratoAsignacionId || desasignarIds.length === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post("/laboral/contratos-repse/desasignar-trabajadores", {
        client_id: clientId,
        contrato_id: contratoAsignacionId,
        trabajador_ids: desasignarIds,
      });
      toast.success(`Desasignados ${data.updated_count || desasignarIds.length} trabajador(es)`);
      setDesasignarIds([]);
      await loadTrabajadoresObra();
      await loadTodosTrabajadores();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al desasignar");
    } finally {
      setBusy(false);
    }
  };

  const onGuardarSdiObra = async () => {
    if (!clientId || !contratoAsignacionId) return;
    const entries = Object.entries(editCuat).filter(([, v]) => v?.sdi || v?.dias_trabajados);
    if (!entries.length) {
      toast.error("Capture SDI o días para al menos un trabajador");
      return;
    }
    setBusy(true);
    try {
      let ok = 0;
      for (const [tid, vals] of entries) {
        await api.put(`/laboral/trabajadores-registro/${tid}/obra-imss`, {
          client_id: clientId,
          contrato_id: contratoAsignacionId,
          sdi: vals.sdi || "",
          dias_trabajados: vals.dias_trabajados || "",
          anio: Number(anioCuat),
          cuatrimestre: cuatrimestreCuat,
        });
        ok += 1;
      }
      toast.success(`Datos cuatrimestrales guardados (${ok})`);
      setEditCuat({});
      await loadTrabajadoresObra();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al guardar SDI/días");
    } finally {
      setBusy(false);
    }
  };

  const onAsignar = async () => {
    if (!clientId || !contratoAsignacionId || selectedIds.length === 0) {
      toast.error("Seleccione contrato y al menos un trabajador");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/laboral/contratos-repse/asignar-trabajadores", {
        client_id: clientId,
        contrato_id: contratoAsignacionId,
        trabajador_ids: selectedIds,
      });
      toast.success(`Asignados ${data.updated_count || selectedIds.length} trabajador(es) a la obra`);
      setSelectedIds([]);
      await loadTrabajadoresObra();
      await loadTodosTrabajadores();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al asignar trabajadores");
    } finally {
      setBusy(false);
    }
  };

  const onRegistrarMovimiento = async () => {
    if (!clientId || !movContratoId || !movTrabajadorId) {
      toast.error("Seleccione contrato, trabajador y tipo de movimiento");
      return;
    }
    setBusy(true);
    try {
      await api.post("/laboral/movimientos-obra-repse", {
        client_id: clientId,
        contrato_id: movContratoId,
        trabajador_id: movTrabajadorId,
        tipo: movTipo,
        fecha: movFecha,
        nota: movNota,
      });
      toast.success(movTipo === "alta" ? "Alta de obra registrada" : "Baja de obra registrada");
      setMovFecha("");
      setMovNota("");
      await loadMovimientos();
      await loadTodosTrabajadores();
      if (contratoAsignacionId === movContratoId) await loadTrabajadoresObra();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al registrar movimiento");
    } finally {
      setBusy(false);
    }
  };

  const labelTrabajador = (id) => {
    const t = todosTrabajadores.find((x) => x.id === id);
    return t ? `${t.nombre_completo || "—"} · ${t.nss || t.curp || "—"}` : id;
  };

  const renderPrestadorPanel = () => {
    if (!prestador?.rfc && !prestador?.razon_social) return null;
    const ubicacion = [prestador.municipio, prestador.estado].filter(Boolean).join(", ");
    return (
      <div
        className="rounded-md border border-[#0F2D5C]/25 bg-[#0F2D5C]/5 p-4 space-y-3"
        data-testid="panel-prestador-repse"
      >
        <div>
          <h4 className="text-sm font-semibold text-[#0F2D5C]">Su cliente — Prestador / contratista REPSE</h4>
          <p className="text-xs text-muted-foreground mt-1">
            Datos tomados de la ficha Clientes. Este es quien presta los servicios especializados;{" "}
            <strong>no</strong> es el contratante de la obra.
          </p>
        </div>
        <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 text-sm">
          <div>
            <dt className="text-xs text-muted-foreground">RFC</dt>
            <dd className="font-mono font-medium">{prestador.rfc || "—"}</dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-xs text-muted-foreground">Razón social</dt>
            <dd className="font-medium">{prestador.razon_social || "—"}</dd>
          </div>
          <div className="sm:col-span-2 lg:col-span-3">
            <dt className="text-xs text-muted-foreground">Domicilio fiscal</dt>
            <dd>{prestador.domicilio_fiscal || "—"}</dd>
          </div>
          {ubicacion ? (
            <div>
              <dt className="text-xs text-muted-foreground">Municipio / Estado</dt>
              <dd>{ubicacion}</dd>
            </div>
          ) : null}
          {prestador.actividad_economica ? (
            <div className="sm:col-span-2">
              <dt className="text-xs text-muted-foreground">Actividad económica</dt>
              <dd>{prestador.actividad_economica}</dd>
            </div>
          ) : null}
        </dl>
      </div>
    );
  };

  const renderContratoFields = (values, setValues, idPrefix = "") => (
    <div className="space-y-6">
      {renderPrestadorPanel()}

      <section className="space-y-4">
        <div>
          <h4 className="text-sm font-semibold">Contratante de la obra</h4>
          <p className="text-xs text-muted-foreground mt-1">
            Empresa que contrató a su cliente para esta obra o proyecto (no confundir con el prestador REPSE).
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}rfc-cont`}>RFC contratante</Label>
            <FieldHint>{hints.rfc_contratante}</FieldHint>
            <Input
              id={`${idPrefix}rfc-cont`}
              value={values.rfc_contratante}
              onChange={(e) => setValues((p) => ({ ...p, rfc_contratante: e.target.value.toUpperCase() }))}
              disabled={!clientId || busy}
              className="font-mono text-sm"
              placeholder="RFC de quien contrató a su cliente"
            />
          </div>
          <div className="space-y-2 md:col-span-1">
            <Label htmlFor={`${idPrefix}razon-cont`}>Razón social contratante</Label>
            <FieldHint>{hints.razon_social_contratante}</FieldHint>
            <Input
              id={`${idPrefix}razon-cont`}
              value={values.razon_social_contratante}
              onChange={(e) => setValues((p) => ({ ...p, razon_social_contratante: e.target.value }))}
              disabled={!clientId || busy}
              placeholder="Nombre de la empresa contratante"
            />
          </div>
        </div>
      </section>

      <section className="space-y-4">
        <div>
          <h4 className="text-sm font-semibold">Obra, patrón IMSS y contrato REPSE</h4>
          <p className="text-xs text-muted-foreground mt-1">
            Datos del proyecto, registro patronal de su cliente y vigencia del contrato.
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}num-contrato`}>Número de contrato</Label>
        <FieldHint>{hints.numero_contrato}</FieldHint>
        <Input
          id={`${idPrefix}num-contrato`}
          value={values.numero_contrato}
          onChange={(e) => setValues((p) => ({ ...p, numero_contrato: e.target.value }))}
          disabled={!clientId || busy}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}folio-repse`}>Folio registro de obra (STPS)</Label>
        <FieldHint>{hints.folio_registro_repse}</FieldHint>
        <Input
          id={`${idPrefix}folio-repse`}
          value={values.folio_registro_repse}
          onChange={(e) => setValues((p) => ({ ...p, folio_registro_repse: e.target.value }))}
          disabled={!clientId || busy}
        />
      </div>
      <div className="space-y-2 md:col-span-2 lg:col-span-1">
        <Label htmlFor={`${idPrefix}razon-obra`}>Nombre de la obra</Label>
        <FieldHint>{hints.razon_social_obra}</FieldHint>
        <Input
          id={`${idPrefix}razon-obra`}
          value={values.razon_social_obra}
          onChange={(e) => setValues((p) => ({ ...p, razon_social_obra: e.target.value }))}
          disabled={!clientId || busy}
        />
      </div>
      <div className="space-y-2">
        <Label>Registro patronal vinculado</Label>
        <FieldHint>{hints.registro_patronal_id}</FieldHint>
        <Select
          value={values.registro_patronal_id || ""}
          onValueChange={(v) => {
            const patron = patronales.find((p) => p.id === v);
            setValues((p) => ({
              ...p,
              registro_patronal_id: v,
              registro_patronal: patron?.registro_patronal || p.registro_patronal,
            }));
          }}
          disabled={!clientId || busy || patronales.length === 0}
        >
          <SelectTrigger>
            <SelectValue placeholder={patronales.length ? "Seleccione patrón" : "Sin patrones activos"} />
          </SelectTrigger>
          <SelectContent>
            {patronales.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.registro_patronal} — {p.nombre_sucursal_obra || p.razon_social_imss || "Patrón"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}rp-texto`}>Registro patronal (texto)</Label>
        <FieldHint>{hints.registro_patronal}</FieldHint>
        <Input
          id={`${idPrefix}rp-texto`}
          value={values.registro_patronal}
          onChange={(e) => setValues((p) => ({ ...p, registro_patronal: e.target.value }))}
          disabled={!clientId || busy}
          className="font-mono text-sm"
        />
      </div>
      <div className="space-y-2">
        <Label>Tipo de prestación</Label>
        <FieldHint>{hints.tipo_prestacion}</FieldHint>
        <Select
          value={values.tipo_prestacion || ""}
          onValueChange={(v) => setValues((p) => ({ ...p, tipo_prestacion: v }))}
          disabled={!clientId || busy}
        >
          <SelectTrigger><SelectValue placeholder="Seleccione" /></SelectTrigger>
          <SelectContent>
            {tipos.map((t) => (
              <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Modalidad de obra</Label>
        <FieldHint>{hints.modalidad_obra}</FieldHint>
        <Select
          value={values.modalidad_obra || ""}
          onValueChange={(v) => setValues((p) => ({ ...p, modalidad_obra: v }))}
          disabled={!clientId || busy}
        >
          <SelectTrigger><SelectValue placeholder="Seleccione" /></SelectTrigger>
          <SelectContent>
            {modalidades.map((m) => (
              <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2 md:col-span-2 lg:col-span-3">
        <Label htmlFor={`${idPrefix}domicilio`}>Domicilio de la obra</Label>
        <FieldHint>{hints.domicilio_obra}</FieldHint>
        <Input
          id={`${idPrefix}domicilio`}
          value={values.domicilio_obra}
          onChange={(e) => setValues((p) => ({ ...p, domicilio_obra: e.target.value }))}
          disabled={!clientId || busy}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}municipio`}>Municipio</Label>
        <FieldHint>{hints.municipio_obra}</FieldHint>
        <Input
          id={`${idPrefix}municipio`}
          value={values.municipio_obra}
          onChange={(e) => setValues((p) => ({ ...p, municipio_obra: e.target.value }))}
          disabled={!clientId || busy}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}estado`}>Estado</Label>
        <FieldHint>{hints.estado_obra}</FieldHint>
        <Input
          id={`${idPrefix}estado`}
          value={values.estado_obra}
          onChange={(e) => setValues((p) => ({ ...p, estado_obra: e.target.value }))}
          disabled={!clientId || busy}
        />
      </div>
      <div className="space-y-2 md:col-span-2">
        <Label htmlFor={`${idPrefix}actividad`}>Actividad específica</Label>
        <FieldHint>{hints.actividad_especifica}</FieldHint>
        <Input
          id={`${idPrefix}actividad`}
          value={values.actividad_especifica}
          onChange={(e) => setValues((p) => ({ ...p, actividad_especifica: e.target.value }))}
          disabled={!clientId || busy}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}num-obra`}>Número obra IMSS</Label>
        <FieldHint>{hints.numero_obra_imss}</FieldHint>
        <Input
          id={`${idPrefix}num-obra`}
          value={values.numero_obra_imss}
          onChange={(e) => setValues((p) => ({ ...p, numero_obra_imss: e.target.value }))}
          disabled={!clientId || busy}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}fecha-ini`}>Fecha inicio</Label>
        <FieldHint>{hints.fecha_inicio}</FieldHint>
        <Input
          id={`${idPrefix}fecha-ini`}
          type="date"
          value={values.fecha_inicio}
          onChange={(e) => setValues((p) => ({ ...p, fecha_inicio: e.target.value }))}
          disabled={!clientId || busy}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}fecha-fin`}>Fecha fin</Label>
        <FieldHint>{hints.fecha_fin}</FieldHint>
        <Input
          id={`${idPrefix}fecha-fin`}
          type="date"
          value={values.fecha_fin}
          onChange={(e) => setValues((p) => ({ ...p, fecha_fin: e.target.value }))}
          disabled={!clientId || busy}
        />
      </div>
        </div>
      </section>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="control-repse-page">
      <PageHeader
        title="Control REPSE"
        subtitle={CONTEXT_LABEL}
        description="Registre obras REPSE: contratante (quien contrató a su cliente), patrón IMSS, trabajadores y bitácora."
      />

      <LaboralFlujoOperativo>
      <div className="space-y-6">
      {config?.instrucciones ? (
        <p className="text-sm text-muted-foreground rounded-md border bg-muted/30 px-4 py-3">
          {config.instrucciones}
        </p>
      ) : null}

      {!clientId && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Seleccione un cliente de trabajo en el Panel General para administrar contratos REPSE.
        </div>
      )}

      <Tabs defaultValue="contratos" className="space-y-4">
        <TabsList>
          <TabsTrigger value="contratos" className="gap-1.5">
            <Briefcase className="h-3.5 w-3.5" />
            Registro de obra
          </TabsTrigger>
          <TabsTrigger value="asignacion" className="gap-1.5">
            <Users className="h-3.5 w-3.5" />
            Patrón y obra
          </TabsTrigger>
          <TabsTrigger value="bitacora" className="gap-1.5">
            <ClipboardList className="h-3.5 w-3.5" />
            Altas y bajas de obra
          </TabsTrigger>
          <TabsTrigger value="relacion" className="gap-1.5" data-testid="tab-relation-contratos-repse">
            <FileSpreadsheet className="h-3.5 w-3.5" />
            Relación de contratos
          </TabsTrigger>
        </TabsList>

        <TabsContent value="contratos" className="space-y-6">
          <section className="rounded-lg border bg-card p-4 md:p-6 space-y-4">
            <div>
              <h3 className="text-sm font-medium">Alta de obra / contrato REPSE</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Registre la obra: indique quién contrató a su cliente (contratante), el domicilio del proyecto,
                patrón IMSS y vigencia. Los datos de su cliente prestador REPSE aparecen arriba.
              </p>
            </div>
            {renderContratoFields(form, setForm, "new-")}
            <div className="flex justify-end pt-2">
              <Button type="button" onClick={onCreate} disabled={!clientId || busy} data-testid="btn-alta-contrato-repse">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                Registrar contrato
              </Button>
            </div>
          </section>

          <section className="rounded-lg border bg-card p-4 md:p-6 space-y-3">
            <h3 className="text-sm font-medium">Contratos activos</h3>
            {loadingTable ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
                <Loader2 className="h-4 w-4 animate-spin" />
                Cargando contratos…
              </div>
            ) : contratos.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                {clientId ? "No hay contratos REPSE. Use el formulario superior." : "Seleccione un cliente."}
              </p>
            ) : (
              <div className="overflow-x-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Contrato</TableHead>
                      <TableHead>Obra</TableHead>
                      <TableHead>Tipo / Modalidad</TableHead>
                      <TableHead>Patrón</TableHead>
                      <TableHead>Ubicación</TableHead>
                      <TableHead>Vigencia</TableHead>
                      <TableHead>Contratante</TableHead>
                      <TableHead className="text-center">PDF</TableHead>
                      <TableHead className="text-right w-[200px]">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contratos.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell className="font-mono text-sm">{row.numero_contrato}</TableCell>
                        <TableCell>
                          <div className="font-medium text-sm">{row.razon_social_obra || "—"}</div>
                          {row.numero_obra_imss ? (
                            <div className="text-[10px] text-muted-foreground font-mono">Obra IMSS {row.numero_obra_imss}</div>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-xs">{labelTipoModalidad(row, tipos, modalidades)}</TableCell>
                        <TableCell className="font-mono text-xs">{row.registro_patronal || "—"}</TableCell>
                        <TableCell className="text-xs">
                          {[row.municipio_obra, row.estado_obra].filter(Boolean).join(", ") || row.domicilio_obra || "—"}
                        </TableCell>
                        <TableCell className="text-xs whitespace-nowrap">
                          {row.fecha_inicio || "—"}
                          {row.fecha_fin ? ` → ${row.fecha_fin}` : ""}
                        </TableCell>
                        <TableCell className="text-xs">{row.rfc_contratante || "—"}</TableCell>
                        <TableCell className="text-center text-xs">
                          {row.tiene_documento_pdf ? (
                            <span className="text-emerald-700">Sí</span>
                          ) : (
                            <span className="text-slate-400">No</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right space-x-1">
                          {row.tiene_documento_pdf ? (
                            <Button type="button" variant="outline" size="sm" onClick={() => onDownloadPdf(row)} disabled={busy}>
                              <Download className="h-3.5 w-3.5 mr-1" />
                              PDF
                            </Button>
                          ) : (
                            <Button type="button" variant="outline" size="sm" onClick={() => triggerUploadPdf(row.id)} disabled={busy}>
                              <Upload className="h-3.5 w-3.5 mr-1" />
                              Subir PDF
                            </Button>
                          )}
                          <Button type="button" variant="outline" size="sm" onClick={() => openEdit(row)} disabled={busy}>
                            <Pencil className="h-3.5 w-3.5 mr-1" />
                            Editar
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => setDeleteRow(row)}
                            disabled={busy}
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-1" />
                            Baja
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </section>
        </TabsContent>

        <TabsContent value="asignacion" className="space-y-4">
          <section className="rounded-lg border bg-card p-4 md:p-6 space-y-4">
            <h3 className="text-sm font-medium">Asignar trabajadores a patrón y obra</h3>
            <p className="text-sm text-muted-foreground">
              Vincule trabajadores del catálogo al contrato/obra REPSE. El patrón se hereda del contrato;
              los asignados alimentan declaraciones cuatrimestrales y confronta IMSS.
            </p>
            <div className="grid gap-4 md:grid-cols-2 max-w-2xl">
              <div className="space-y-2">
                <Label>Contrato / obra</Label>
                <FieldHint>Seleccione la obra a la que se vincularán los trabajadores</FieldHint>
                <Select
                  value={contratoAsignacionId}
                  onValueChange={setContratoAsignacionId}
                  disabled={!clientId || contratos.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccione contrato" />
                  </SelectTrigger>
                  <SelectContent>
                    {contratos.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.numero_contrato} — {c.razon_social_obra || "Obra"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {contratoAsignacionId ? (
              <>
                <div className="grid gap-6 lg:grid-cols-2">
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium">En esta obra ({trabajadoresObra.length})</h4>
                    {loadingAsignacion ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Cargando…
                      </div>
                    ) : trabajadoresObra.length === 0 ? (
                      <p className="text-sm text-muted-foreground">Sin trabajadores asignados aún.</p>
                    ) : (
                      <ul className="text-sm border rounded-md divide-y max-h-48 overflow-y-auto">
                        {trabajadoresObra.map((t) => (
                          <li key={t.id} className="px-3 py-2">
                            {t.nombre_completo || "—"} · NSS {t.nss || "—"}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium">Agregar desde registro ({trabajadoresDisponibles.length})</h4>
                    {trabajadoresDisponibles.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        No hay trabajadores disponibles. Regístrelos en Inscripción SAT o IMSS.
                      </p>
                    ) : (
                      <ul className="text-sm border rounded-md divide-y max-h-48 overflow-y-auto">
                        {trabajadoresDisponibles.map((t) => (
                          <li key={t.id} className="px-3 py-2 flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(t.id)}
                              onChange={() => toggleSelect(t.id)}
                              className="rounded border-input"
                            />
                            <span>{t.nombre_completo || "—"} · {t.rfc || t.curp || "—"}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button
                    type="button"
                    onClick={onAsignar}
                    disabled={!clientId || busy || selectedIds.length === 0}
                  >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Users className="h-4 w-4 mr-2" />}
                    Asignar seleccionados
                  </Button>
                </div>
              </>
            ) : null}
          </section>
        </TabsContent>

        <TabsContent value="bitacora" className="space-y-4">
          <section className="rounded-lg border bg-card p-4 md:p-6 space-y-4">
            <h3 className="text-sm font-medium">Registrar alta o baja en obra</h3>
            <p className="text-sm text-muted-foreground">
              Bitácora operativa de movimientos por contrato REPSE. Una alta asigna al trabajador a la obra;
              una baja lo desvincula del contrato.
            </p>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 max-w-4xl">
              <div className="space-y-2">
                <Label>Contrato / obra</Label>
                <Select
                  value={movContratoId}
                  onValueChange={setMovContratoId}
                  disabled={!clientId || contratos.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccione contrato" />
                  </SelectTrigger>
                  <SelectContent>
                    {contratos.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.numero_contrato} — {c.razon_social_obra || "Obra"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Trabajador</Label>
                <Select
                  value={movTrabajadorId}
                  onValueChange={setMovTrabajadorId}
                  disabled={!clientId || todosTrabajadores.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccione trabajador" />
                  </SelectTrigger>
                  <SelectContent>
                    {todosTrabajadores.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.nombre_completo || "—"} · {t.nss || t.curp || "—"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select value={movTipo} onValueChange={setMovTipo} disabled={!clientId}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="alta">Alta en obra</SelectItem>
                    <SelectItem value="baja">Baja de obra</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="mov-fecha">Fecha</Label>
                <Input
                  id="mov-fecha"
                  type="date"
                  value={movFecha}
                  onChange={(e) => setMovFecha(e.target.value)}
                  disabled={!clientId || busy}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="mov-nota">Nota (opcional)</Label>
                <Input
                  id="mov-nota"
                  value={movNota}
                  onChange={(e) => setMovNota(e.target.value)}
                  disabled={!clientId || busy}
                  placeholder="Motivo o referencia interna"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                type="button"
                onClick={onRegistrarMovimiento}
                disabled={!clientId || busy || !movContratoId || !movTrabajadorId}
                data-testid="btn-registrar-movimiento-obra"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ClipboardList className="h-4 w-4 mr-2" />}
                Registrar movimiento
              </Button>
            </div>
          </section>

          <section className="rounded-lg border bg-card p-4 md:p-6 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-medium">Historial de movimientos</h3>
              {movContratoId ? (
                <span className="text-xs text-muted-foreground">Filtrado por contrato seleccionado arriba</span>
              ) : null}
            </div>
            {loadingMov ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
                <Loader2 className="h-4 w-4 animate-spin" />
                Cargando bitácora…
              </div>
            ) : movimientos.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                {clientId ? "Sin movimientos registrados." : "Seleccione un cliente."}
              </p>
            ) : (
              <div className="overflow-x-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Contrato</TableHead>
                      <TableHead>Trabajador</TableHead>
                      <TableHead>Nota</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {movimientos.map((m) => (
                      <TableRow key={m.id}>
                        <TableCell className="text-sm">{m.fecha || "—"}</TableCell>
                        <TableCell className="text-sm capitalize">{m.tipo === "baja" ? "Baja" : "Alta"}</TableCell>
                        <TableCell className="font-mono text-xs">{m.numero_contrato || "—"}</TableCell>
                        <TableCell className="text-sm">{labelTrabajador(m.trabajador_id)}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{m.nota || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </section>
        </TabsContent>

        <TabsContent value="relacion" className="space-y-4">
          <section className="rounded-lg border bg-card p-4 md:p-6 space-y-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <h3 className="text-sm font-medium">Relación de contratos REPSE</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Consulte todos los contratos/obra del cliente, filtre por estatus y exporte a Excel para control y validación.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={onExportExcel}
                disabled={!clientId || busy || !contratosRelacionFiltrados.length}
                data-testid="btn-export-excel-contratos-repse"
              >
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}
                Exportar Excel
              </Button>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
              <div className="space-y-1 flex-1 min-w-[220px]">
                <Label className="text-xs text-muted-foreground">Buscar contrato u obra</Label>
                <Input
                  value={searchRelacionInput}
                  onChange={(e) => setSearchRelacionInput(e.target.value)}
                  placeholder="Número de contrato, nombre de obra, RFC contratante…"
                  disabled={!clientId}
                  data-testid="input-buscar-contratos-repse"
                />
              </div>
              <div className="space-y-1 min-w-[160px]">
                <Label className="text-xs text-muted-foreground">Estatus</Label>
                <Select value={filterEstatusRelacion} onValueChange={setFilterEstatusRelacion} disabled={!clientId}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    <SelectItem value="abierto">Abierto</SelectItem>
                    <SelectItem value="cerrado">Cerrado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {loadingRelacion ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
                <Loader2 className="h-4 w-4 animate-spin" />
                Cargando relación…
              </div>
            ) : contratosRelacionFiltrados.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                {clientId ? "No hay contratos que coincidan con la búsqueda." : "Seleccione un cliente."}
              </p>
            ) : (
              <div className="overflow-x-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Núm. contrato</TableHead>
                      <TableHead>Obra</TableHead>
                      <TableHead>Estatus</TableHead>
                      <TableHead>Inicio</TableHead>
                      <TableHead>Término</TableHead>
                      <TableHead>Patrón</TableHead>
                      <TableHead>Contratante</TableHead>
                      <TableHead className="text-center">PDF</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contratosRelacionFiltrados.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell className="font-mono text-sm">{row.numero_contrato}</TableCell>
                        <TableCell className="text-sm">{row.razon_social_obra || "—"}</TableCell>
                        <TableCell>
                          <span
                            className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-medium ${
                              row.estatus_obra === "cerrado"
                                ? "bg-amber-100 text-amber-900"
                                : "bg-emerald-100 text-emerald-800"
                            }`}
                          >
                            {row.estatus_obra_label || row.estatus_obra || "—"}
                          </span>
                        </TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{row.fecha_inicio || "—"}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{row.fecha_fin || "—"}</TableCell>
                        <TableCell className="font-mono text-xs">{row.registro_patronal || "—"}</TableCell>
                        <TableCell className="text-xs">{row.rfc_contratante || "—"}</TableCell>
                        <TableCell className="text-center text-xs">
                          {row.tiene_documento_pdf ? "Sí" : "No"}
                        </TableCell>
                        <TableCell className="text-right space-x-1">
                          {row.tiene_documento_pdf ? (
                            <Button type="button" variant="ghost" size="sm" onClick={() => onDownloadPdf(row)} disabled={busy}>
                              <Download className="h-3.5 w-3.5" />
                            </Button>
                          ) : null}
                          <Button type="button" variant="outline" size="sm" onClick={() => openEdit(row)} disabled={busy}>
                            Editar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </section>
        </TabsContent>
      </Tabs>

      </div>
      </LaboralFlujoOperativo>

      <input
        ref={pdfInputRef}
        type="file"
        accept=".pdf,application/pdf"
        className="hidden"
        onChange={(e) => onPdfSelected(e.target.files?.[0])}
      />

      <Dialog open={Boolean(editRow)} onOpenChange={(open) => !open && setEditRow(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar contrato REPSE</DialogTitle>
          </DialogHeader>
          {renderContratoFields(editForm, setEditForm, "edit-")}
          <div className="rounded-md border border-dashed p-4 space-y-3">
            <p className="text-sm font-medium m-0">Contrato REPSE en PDF</p>
            <p className="text-xs text-muted-foreground m-0">
              Suba el contrato firmado o escaneado para consulta y descarga posterior.
            </p>
            {editRow?.tiene_documento_pdf ? (
              <p className="text-xs text-emerald-800 m-0">
                Archivo cargado: {editRow.documento_filename || "PDF"}
                {editRow.documento_uploaded_at ? ` · ${editRow.documento_uploaded_at.slice(0, 10)}` : ""}
              </p>
            ) : (
              <p className="text-xs text-amber-800 m-0">Sin PDF cargado para este contrato.</p>
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() => triggerUploadPdf(editRow?.id)}
              >
                <Upload className="h-3.5 w-3.5 mr-1" />
                {editRow?.tiene_documento_pdf ? "Reemplazar PDF" : "Subir PDF"}
              </Button>
              {editRow?.tiene_documento_pdf ? (
                <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => onDownloadPdf(editRow)}>
                  <Download className="h-3.5 w-3.5 mr-1" />
                  Descargar PDF
                </Button>
              ) : null}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditRow(null)} disabled={busy}>Cancelar</Button>
            <Button onClick={onSaveEdit} disabled={busy}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(deleteRow)} onOpenChange={(open) => !open && setDeleteRow(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Desactivar contrato?</AlertDialogTitle>
            <AlertDialogDescription>
              El contrato {deleteRow?.numero_contrato} dejará de aparecer en selectores activos.
              Los trabajadores conservan su vínculo histórico.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} disabled={busy} className="bg-destructive text-destructive-foreground">
              Desactivar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
